import React from 'react';
import { Card, Typography, Progress, Row, Col, Divider } from 'antd';
import { PatternPoint } from '../types';
import './TrendSummary.css';
import { useI18n } from '../i18n/I18nProvider';

const { Title, Paragraph, Text } = Typography;

interface TrendSummaryProps {
  pattern: PatternPoint | null;
}

const TrendSummary: React.FC<TrendSummaryProps> = ({ pattern }) => {
  const { t } = useI18n();

  if (!pattern) {
    return null;
  }

  // 计算目标位和拐点到达情况
  const targetPointsTotal = [1, 2, 4, 5, 7, 8].length;
  const pivotPointsTotal = [3, 6, 9].length;
  
  const reachedTargetPoints = pattern.predictionLines
    .filter(line => [1, 2, 4, 5, 7, 8].includes(line.lineNumber) && line.reached)
    .length;
  
  const reachedPivotPoints = pattern.predictionLines
    .filter(line => [3, 6, 9].includes(line.lineNumber) && line.reached)
    .length;
  
  // 计算进度百分比
  const targetProgress = Math.round((reachedTargetPoints / targetPointsTotal) * 100);
  const pivotProgress = Math.round((reachedPivotPoints / pivotPointsTotal) * 100);
  
  // 根据到达情况生成分析结论
  const generateTrendAnalysis = () => {
    const mode = pattern.type === 'top' ? t('chart.mode.top') : t('chart.mode.bottom');
    const pivotPoints = pattern.predictionLines.filter(line => [3, 6, 9].includes(line.lineNumber));
    
    // 检查是否到达拐点
    const reachedPivot1 = pivotPoints[0]?.reached || false;
    const reachedPivot2 = pivotPoints[1]?.reached || false;
    const reachedPivot3 = pivotPoints[2]?.reached || false;
    
    if (pattern.type === 'top') {
      // 顶分型（下跌）趋势分析
      if (reachedPivot3) {
        return `${mode}趋势已运行至第三拐点附近，波段空间接近尾端，需防范结构性反转与回补风险。`;
      } else if (reachedPivot2) {
        return `${mode}趋势已推进至第二拐点附近，后续空间相对收敛，关注第三拐点的承接强度。`;
      } else if (reachedPivot1) {
        return `${mode}趋势进入确认阶段，第一拐点已达；若结构不被破坏，下一关注区在第二拐点。`;
      } else {
        return `${mode}趋势处于早期运行段，首个拐点尚未触达；短期更应重视波动扩张与假突破的噪音。`;
      }
    } else {
      // 底分型（上涨）趋势分析
      if (reachedPivot3) {
        return `${mode}趋势已运行至第三拐点附近，波段空间接近尾端，需防范获利回吐与结构性回调。`;
      } else if (reachedPivot2) {
        return `${mode}趋势已推进至第二拐点附近，后续空间相对收敛，关注第三拐点的供给压力。`;
      } else if (reachedPivot1) {
        return `${mode}趋势进入确认阶段，第一拐点已达；若结构不被破坏，下一关注区在第二拐点。`;
      } else {
        return `${mode}趋势处于早期运行段，首个拐点尚未触达；短期更应重视波动扩张与假突破的噪音。`;
      }
    }
  };
  
  // 生成操作建议
  const generateActionSuggestion = () => {
    const reachedTargets = pattern.predictionLines
      .filter(line => [1, 2, 4, 5, 7, 8].includes(line.lineNumber) && line.reached)
      .map(line => {
        const supportPoints = [1, 2, 4, 5, 7, 8];
        return supportPoints.indexOf(line.lineNumber) + 1;
      });
    
    const remainingTargets = pattern.predictionLines
      .filter(line => [1, 2, 4, 5, 7, 8].includes(line.lineNumber) && !line.reached)
      .map(line => {
        const supportPoints = [1, 2, 4, 5, 7, 8];
        return supportPoints.indexOf(line.lineNumber) + 1;
      });
    
    if (pattern.type === 'top') {
      // 顶分型（下跌）操作建议
      if (reachedTargets.length === 0) {
        return `以“目标位1/2”为首批观察区，优先评估量价配合与回抽力度；仓位管理以试探为主。`;
      } else if (reachedTargets.length <= 2) {
        return `目标位${reachedTargets.join('、')}已触达；若结构延续，可将目标位${remainingTargets[0]}设为下一阶段的检验点，并同步上移风控线。`;
      } else if (reachedTargets.length <= 4) {
        return `多目标位已兑现，波段处在中后段；可在“第三拐点”邻域做分批止盈与风险收敛。`;
      } else {
        return `目标位兑现接近饱和；继续追击的边际收益偏低，建议以减仓、锁盈与等待再结构为主。`;
      }
    } else {
      // 底分型（上涨）操作建议
      if (reachedTargets.length === 0) {
        return `以“目标位1/2”为首批观察区，优先评估放量突破与回踩确认；仓位管理以试探为主。`;
      } else if (reachedTargets.length <= 2) {
        return `目标位${reachedTargets.join('、')}已触达；若结构延续，可将目标位${remainingTargets[0]}设为下一阶段的检验点，并同步上移风控线。`;
      } else if (reachedTargets.length <= 4) {
        return `多目标位已兑现，波段处在中后段；可在“第三拐点”邻域做分批止盈与风险收敛。`;
      } else {
        return `目标位兑现接近饱和；继续追击的边际收益偏低，建议以减仓、锁盈与等待再结构为主。`;
      }
    }
  };
  
  const trendAnalysis = generateTrendAnalysis();
  const actionSuggestion = generateActionSuggestion();
  
  // 根据模式类型确定颜色
  const getThemeColor = () => {
    return pattern.type === 'top' ? '#ef5350' : '#26a69a';
  };
  
  return (
    <Card className="trend-summary-card" title={t('summary.title')}>
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Title level={4} style={{ color: getThemeColor() }}>
            {t('summary.section.analysis', { mode: pattern.type === 'top' ? t('chart.mode.top') : t('chart.mode.bottom') })}
          </Title>
          <Paragraph className="trend-analysis">
            {trendAnalysis}
          </Paragraph>
        </Col>
        
        <Col span={12}>
          <Text strong>{t('summary.progress.targets')}</Text>
          <Progress 
            percent={targetProgress} 
            status={targetProgress === 100 ? "success" : "active"}
            strokeColor={getThemeColor()}
          />
          <Text type="secondary">
            {t('summary.reached.targets', { reached: reachedTargetPoints, total: targetPointsTotal })}
          </Text>
        </Col>
        
        <Col span={12}>
          <Text strong>{t('summary.progress.pivots')}</Text>
          <Progress 
            percent={pivotProgress} 
            status={pivotProgress === 100 ? "success" : "active"}
            strokeColor={getThemeColor()}
          />
          <Text type="secondary">
            {t('summary.reached.pivots', { reached: reachedPivotPoints, total: pivotPointsTotal })}
          </Text>
        </Col>
        
        <Divider />
        
        <Col span={24}>
          <Title level={5}>{t('summary.section.action')}</Title>
          <Paragraph className="action-suggestion">
            {actionSuggestion}
          </Paragraph>
          <Text type="secondary" className="disclaimer">
            {t('summary.disclaimer')}
          </Text>
        </Col>
      </Row>
    </Card>
  );
};

export default TrendSummary; 